import React from 'react'
import { Clock, ExternalLink } from 'lucide-react'
import styles from './VisitorTimeline.module.scss'
import type { VisitorJourneyEvent } from '@/types/analytics'

interface VisitorTimelineProps {
  events: VisitorJourneyEvent[]
  className?: never
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function formatTimeDiff(msA: number, msB: number): string {
  const diff = msB - msA
  if (diff < 1000) return '<1s'
  if (diff < 60_000) return `${Math.round(diff / 1000)}s`
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m`
  return `${(diff / 3_600_000).toFixed(1)}h`
}

function dotClassName(index: number, total: number): string {
  const classes = [styles.dot]
  if (index === 0) classes.push(styles.dotFirst)
  if (index === total - 1 && total > 1) classes.push(styles.dotLast)
  return classes.join(' ')
}

export const VisitorTimeline: React.FC<VisitorTimelineProps> = ({ events }) => {
  if (events.length === 0) {
    return <div className={styles.empty}>No page views recorded for this visitor.</div>
  }

  return (
    <div className={styles.timeline}>
      {events.map((event, index) => {
        const prevTimestamp = index > 0 ? new Date(events[index - 1].timestamp).getTime() : null
        const currentTimestamp = new Date(event.timestamp).getTime()

        return (
          <div key={index} className={styles.entry}>
            <div className={styles.dotColumn}>
              <span className={dotClassName(index, events.length)} />
              <span className={styles.connector} />
            </div>
            <div className={styles.content}>
              <div className={styles.pathRow}>
                <span className={styles.stepNumber}>{index + 1}</span>
                <span className={styles.path}>{event.path}</span>
              </div>
              <div className={styles.meta}>
                <span className={styles.timestamp}>
                  <span className={styles.metaIcon}><Clock /></span>
                  {formatTime(event.timestamp)}
                </span>
                {prevTimestamp !== null && (
                  <span className={styles.timeDiff}>
                    +{formatTimeDiff(prevTimestamp, currentTimestamp)}
                  </span>
                )}
                {event.referrer && (
                  <span className={styles.referrer}>
                    <span className={styles.metaIcon}><ExternalLink /></span>
                    <span className={styles.referrerLabel}>{event.referrer}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
